/**
 * NSW Cadastre Service
 *
 * Queries two NSW spatial services to find subdivision lots:
 *
 *  1. NSW SIX Cadastre (registered lots):
 *     sixmaps/Cadastre/MapServer/0 — lots already registered with LRS
 *
 *  2. NSW Land Subdivision Pipeline (approved, not-yet-registered lots):
 *     portal.spatial.nsw.gov.au/.../NSW_Land_Subdivision_Pipeline_Production/FeatureServer/0
 *     Contains lots from approved Development/Subdivision Certificate applications
 *     that haven't yet been formally registered in the cadastre.
 *
 * Both services return geometry in Web Mercator (WKID 102100/3857), which we
 * convert client-side to WGS84. Input geometry is also sent in Web Mercator to
 * avoid server-side coordinate transformation (which causes 500 errors).
 */

import type { EstateJsonData } from '@types';

// sixmaps/Cadastre/MapServer/0 — registered lots, Query-capable.
// (public/NSW_Cadastre/MapServer/9 looks identical but Query is disabled → 500)
const NSW_SIX_URL =
  'https://maps.six.nsw.gov.au/arcgis/rest/services/sixmaps/Cadastre/MapServer/0/query';

// NSW Land Subdivision Pipeline — approved but not-yet-registered lots.
const NSW_PIPELINE_URL =
  'https://portal.spatial.nsw.gov.au/server/rest/services/Hosted/NSW_Land_Subdivision_Pipeline_Production/FeatureServer/0/query';

// ─── Internal types ───────────────────────────────────────────────────────────

interface NswLotProperties {
  planlabel?: string;
  lotnumber?: string;
  planlotarea?: number | null;
  planlotareaunits?: string | null;
  // ArcGIS may return any additional fields when using outFields=*
  [key: string]: unknown;
}

/** Raw ArcGIS JSON feature from the service */
interface ArcGisFeature {
  attributes: NswLotProperties;
  geometry: {
    rings: number[][][];               // [[[lng, lat], ...], ...]  with outSR=4326
    spatialReference?: { wkid: number };
  } | null;
}

/**
 * Internal normalised feature — geometry uses GeoJSON-style coordinates
 * (same format as ArcGIS rings with outSR=4326, so no conversion needed).
 */
interface NswFeature {
  type: 'Feature';
  properties: NswLotProperties;
  geometry: {
    type: 'Polygon';
    coordinates: number[][][];   // [outerRing, ...holes]  each ring is [[lng,lat],...]
  } | null;
}

interface NswBbox {
  xmin: number;
  ymin: number;
  xmax: number;
  ymax: number;
}

// ─── Coordinate conversion ────────────────────────────────────────────────────

/**
 * Convert Web Mercator metres [x, y] to WGS84 [lng, lat] degrees.
 * NSW_Cadastre serves in WKID 102100 (3857) natively.
 */
function webMercatorToLngLat(x: number, y: number): [number, number] {
  const lng = (x * 180) / 20037508.342789244;
  const lat =
    (Math.atan(Math.exp((Math.PI * y) / 20037508.342789244)) * 360) / Math.PI - 90;
  return [lng, lat];
}

/**
 * Convert WGS84 [lng, lat] degrees to Web Mercator metres [x, y].
 * Used to send input geometry in the service's native CRS, avoiding any
 * server-side coordinate transformation (which causes 500 errors).
 */
function lngLatToWebMercator(lng: number, lat: number): [number, number] {
  const x = (lng * 20037508.342789244) / 180;
  const y =
    Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)) *
    (20037508.342789244 / Math.PI);
  return [x, y];
}

/**
 * Normalise ArcGIS rings to [lng, lat] degrees based on the response's wkid.
 * Geographic CRIs (4326, 4283) are already in degrees — no conversion needed.
 */
function normaliseRings(rings: number[][][], wkid: number): number[][][] {
  if (wkid === 102100 || wkid === 3857) {
    return rings.map((ring) => ring.map(([x, y]) => webMercatorToLngLat(x, y)));
  }
  return rings;
}

// ─── Low-level helpers ────────────────────────────────────────────────────────

function generateHandleId(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from({ length: 5 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

/**
 * Return the outer ring of a Polygon or the largest outer ring of a MultiPolygon.
 * Rings are arrays of [lng, lat] pairs.
 */
function getOuterRing(geometry: NswFeature['geometry']): number[][] | null {
  if (!geometry) return null;
  if (geometry.type === 'Polygon') {
    return (geometry.coordinates as number[][][])[0] ?? null;
  }
  // MultiPolygon — take the polygon with the most vertices (proxy for largest)
  const polygons = geometry.coordinates as unknown as number[][][][];
  let largest: number[][] | null = null;
  for (const poly of polygons) {
    const ring = poly[0];
    if (ring && (!largest || ring.length > largest.length)) {
      largest = ring;
    }
  }
  return largest;
}

/**
 * Convert a GeoJSON ring ([lng, lat] pairs) to BlocPlus {lat, lng} points.
 * Ensures the polygon is closed (first point === last point).
 */
function ringToPoints(ring: number[][]): { lat: number; lng: number }[] {
  const points = ring.map(([lng, lat]) => ({ lat, lng }));
  if (points.length > 1) {
    const first = points[0];
    const last = points[points.length - 1];
    if (first.lat !== last.lat || first.lng !== last.lng) {
      points.push({ lat: first.lat, lng: first.lng });
    }
  }
  return points;
}

/**
 * Compute the bounding box of a polygon's outer ring.
 */
function getBbox(geometry: NswFeature['geometry']): NswBbox | null {
  const ring = getOuterRing(geometry);
  if (!ring || ring.length === 0) return null;
  let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity;
  for (const [lng, lat] of ring) {
    if (lng < xmin) xmin = lng;
    if (lng > xmax) xmax = lng;
    if (lat < ymin) ymin = lat;
    if (lat > ymax) ymax = lat;
  }
  return { xmin, ymin, xmax, ymax };
}

/**
 * Ray-casting point-in-polygon test.
 * Returns true if [lng, lat] is strictly inside the ring.
 * Winding order doesn't matter — ray casting counts boundary crossings.
 */
function pointInRing(lng: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Compute the union bounding box across multiple lots (covers all of them).
 */
function computeUnionBbox(lots: NswFeature[]): NswBbox | null {
  let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity;
  let hasData = false;
  for (const lot of lots) {
    const b = getBbox(lot.geometry);
    if (!b) continue;
    hasData = true;
    if (b.xmin < xmin) xmin = b.xmin;
    if (b.ymin < ymin) ymin = b.ymin;
    if (b.xmax > xmax) xmax = b.xmax;
    if (b.ymax > ymax) ymax = b.ymax;
  }
  return hasData ? { xmin, ymin, xmax, ymax } : null;
}

/**
 * Centroid of a ring (simple average of vertices).
 */
function ringCentroid(ring: number[][]): { lat: number; lng: number } {
  const n = ring.length;
  const sumLng = ring.reduce((s, [lng]) => s + lng, 0);
  const sumLat = ring.reduce((s, [, lat]) => s + lat, 0);
  return { lat: sumLat / n, lng: sumLng / n };
}

// ─── NSW SIX API query ────────────────────────────────────────────────────────

async function queryNswSix(params: Record<string, string>): Promise<NswFeature[]> {
  // GET request — outSR intentionally omitted (service returns 500 on coordinate
  // transformation); we convert Web Mercator → WGS84 client-side instead.
  const searchParams = new URLSearchParams({
    outFields: '*',
    f: 'json',
    returnGeometry: 'true',
    ...params,
  });

  const response = await fetch(`${NSW_SIX_URL}?${searchParams.toString()}`);
  const text = await response.text();

  if (!response.ok) {
    throw new Error(
      `NSW SIX API error: ${response.status} ${response.statusText} — ${text.substring(0, 300)}`
    );
  }

  let data: {
    features?: ArcGisFeature[];
    error?: { message?: string };
    spatialReference?: { wkid: number; latestWkid?: number };
  };
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`NSW SIX API: unexpected response: ${text.substring(0, 200)}`);
  }

  if (data.error) {
    throw new Error(
      `NSW SIX API query failed: ${data.error.message ?? JSON.stringify(data.error)}`
    );
  }

  // Prefer latestWkid (e.g. 3857 for Web Mercator, canonical alias of 102100)
  const wkid = data.spatialReference?.latestWkid ?? data.spatialReference?.wkid ?? 4326;

  return (data.features ?? []).map((f) => ({
    type: 'Feature' as const,
    properties: f.attributes,
    geometry: f.geometry
      ? { type: 'Polygon' as const, coordinates: normaliseRings(f.geometry.rings, wkid) }
      : null,
  }));
}

/**
 * Query the NSW Land Subdivision Pipeline for approved (but not-yet-registered)
 * lots within a WGS84 bounding box.
 *
 * The Pipeline FeatureServer supports inSR/outSR=4326 natively, so we send
 * WGS84 coordinates directly and receive WGS84 geometry back — no Web Mercator
 * conversion needed on either end.
 *
 * Normalises Pipeline field names to the shared NswLotProperties shape:
 *   preallocatedplannumber → planlabel  (falls back to caseid when null)
 *   parcelname             → lotnumber
 *   parcelarea (string m²) → planlotarea (number)
 *
 * Errors are swallowed — pipeline data is supplementary; the search continues
 * with cadastre results alone if the pipeline service is unavailable.
 */
async function queryNswPipeline(wgs84Bbox: NswBbox): Promise<NswFeature[]> {
  const all: NswFeature[] = [];
  let offset = 0;
  const pageSize = 1000;

  // Comma-separated format matches the format the Pipeline service expects for
  // WGS84 envelope queries (confirmed working: "xmin,ymin,xmax,ymax").
  const geometryStr =
    `${wgs84Bbox.xmin},${wgs84Bbox.ymin},${wgs84Bbox.xmax},${wgs84Bbox.ymax}`;

  try {
    while (true) {
      const searchParams = new URLSearchParams({
        geometry: geometryStr,
        geometryType: 'esriGeometryEnvelope',
        inSR: '4326',
        outSR: '4326',
        spatialRel: 'esriSpatialRelIntersects',
        where: "casestatus='Determined' AND determinationoutcome='Approved'",
        outFields: 'preallocatedplannumber,parcelname,parcelarea,caseid,surveydescription',
        f: 'json',
        returnGeometry: 'true',
        resultOffset: String(offset),
        resultRecordCount: String(pageSize),
      });

      const response = await fetch(`${NSW_PIPELINE_URL}?${searchParams.toString()}`);
      if (!response.ok) break;

      const data = await response.json() as {
        features?: Array<{
          attributes: {
            preallocatedplannumber?: string | null;
            parcelname?: string | null;
            parcelarea?: string | number | null;
            caseid?: string | null;
            surveydescription?: string | null;
          };
          geometry: { rings: number[][][]; spatialReference?: { wkid: number } } | null;
        }>;
        error?: unknown;
        spatialReference?: { wkid: number; latestWkid?: number };
      };
      if (data.error || !data.features) break;

      // With outSR=4326 the rings are already in WGS84; normaliseRings is a
      // no-op for wkid=4326 (it only converts 102100/3857).
      const wkid = data.spatialReference?.latestWkid ?? data.spatialReference?.wkid ?? 4326;

      for (const f of data.features) {
        const attrs = f.attributes;
        // Use preallocatedplannumber when available; fall back to caseid so
        // lots without a DP number are still grouped and included via PIP.
        const planLabel = (attrs.preallocatedplannumber ?? attrs.caseid)?.toUpperCase();
        if (!planLabel) continue;

        all.push({
          type: 'Feature' as const,
          properties: {
            planlabel: planLabel,
            lotnumber: String(attrs.parcelname ?? ''),
            planlotarea: parseFloat(String(attrs.parcelarea ?? '0')) || 0,
            planlotareaunits: 'm2',
          },
          geometry: f.geometry
            ? { type: 'Polygon' as const, coordinates: normaliseRings(f.geometry.rings, wkid) }
            : null,
        });
      }

      if (data.features.length < pageSize) break;
      offset += pageSize;
    }
  } catch {
    // Pipeline is supplementary — silently ignore errors
  }

  return all;
}

// ─── Public API functions ─────────────────────────────────────────────────────

/**
 * Find the parent lot by lot number and plan label.
 * e.g. lotNumber="1", planLabel="DP831464"
 */
export async function findParentLot(
  lotNumber: string,
  planLabel: string
): Promise<NswFeature> {
  const features = await queryNswSix({
    where: `planlabel='${planLabel.toUpperCase()}' AND lotnumber='${lotNumber}'`,
    resultRecordCount: '1',
  });

  if (features.length === 0) {
    throw new Error(
      `Parent lot not found: Lot ${lotNumber} ${planLabel.toUpperCase()}. ` +
        `Please check the lot number and plan number are correct.`
    );
  }

  return features[0];
}

/**
 * Find ALL lots belonging to a plan label.
 * Used when no specific lot number is given — searches across all parent lots.
 */
async function findAllParentLots(planLabel: string): Promise<NswFeature[]> {
  const features = await queryNswSix({
    where: `planlabel='${planLabel.toUpperCase()}'`,
  });

  if (features.length === 0) {
    throw new Error(
      `Plan not found: ${planLabel.toUpperCase()}. ` +
        `Please check the plan number is correct.`
    );
  }

  return features;
}

/**
 * Find the cadastral lot at a given lat/lng point.
 * Used for address-based NSW lookup.
 */
export async function findLotAtPoint(
  lat: number,
  lng: number
): Promise<{ lotNumber: string; planLabel: string; area: number } | null> {
  const [mx, my] = lngLatToWebMercator(lng, lat);
  const features = await queryNswSix({
    geometry: JSON.stringify({ x: mx, y: my }),
    geometryType: 'esriGeometryPoint',
    spatialRel: 'esriSpatialRelIntersects',
    where: '1=1',
    resultRecordCount: '1',
  });

  if (features.length === 0) return null;

  const props = features[0].properties;
  return {
    lotNumber: props.lotnumber ?? '1',
    planLabel: props.planlabel ?? '',
    area: props.planlotarea ?? 0,
  };
}

/**
 * Fetch all lots within the union bounding box of the parent lots.
 * Queries both the NSW SIX Cadastre (registered lots) and the NSW Land
 * Subdivision Pipeline (approved, not-yet-registered lots) and merges results.
 * Neighbouring parcels are filtered out later by detectSubdivisionPlans.
 * Cadastre results are paginated automatically if they exceed 1000.
 */
async function fetchLotsInParent(
  parentLots: NswFeature[],
  parentPlanLabel: string
): Promise<NswFeature[]> {
  const bbox = computeUnionBbox(parentLots);
  if (!bbox) {
    throw new Error('Could not extract bounding box from parent lots.');
  }

  // Convert bbox from WGS84 degrees → Web Mercator metres so both services
  // receive input in their native CRS (avoids 500 errors from inSR=4326).
  const [xmin, ymin] = lngLatToWebMercator(bbox.xmin, bbox.ymin);
  const [xmax, ymax] = lngLatToWebMercator(bbox.xmax, bbox.ymax);
  const wmBbox = { xmin, ymin, xmax, ymax };
  const geometryParam = JSON.stringify(wmBbox);

  // ── Cadastre (registered lots) ──────────────────────────────────────────
  const cadastreLots: NswFeature[] = [];
  let offset = 0;
  const pageSize = 1000;

  while (true) {
    const features = await queryNswSix({
      geometry: geometryParam,
      geometryType: 'esriGeometryEnvelope',
      spatialRel: 'esriSpatialRelIntersects',
      where: `planlabel<>'${parentPlanLabel.toUpperCase()}'`,
      resultOffset: String(offset),
      resultRecordCount: String(pageSize),
    });

    cadastreLots.push(...features);
    if (features.length < pageSize) break;
    offset += pageSize;
  }

  // ── Pipeline (approved, not-yet-registered lots) ─────────────────────────
  // Pipeline supports inSR/outSR=4326 natively — pass WGS84 bbox directly.
  const pipelineLots = await queryNswPipeline(bbox);

  // Merge — deduplicate by plan+lot so registered cadastre lots win over
  // pipeline entries for the same parcel (pipeline lags behind registration).
  const seen = new Set<string>();
  const merged: NswFeature[] = [];
  for (const lot of cadastreLots) {
    const key = `${lot.properties.planlabel ?? ''}|${lot.properties.lotnumber ?? ''}`;
    seen.add(key);
    merged.push(lot);
  }
  for (const lot of pipelineLots) {
    const key = `${lot.properties.planlabel ?? ''}|${lot.properties.lotnumber ?? ''}`;
    if (!seen.has(key)) merged.push(lot);
  }

  // Client-side PIP filter: only keep lots whose centroid is inside a parent lot.
  // This accurately excludes neighbouring parcels regardless of bbox overlap.
  // Ray casting is winding-order-agnostic, so Web Mercator→WGS84 rings work fine.
  const parentRings = parentLots
    .map((l) => getOuterRing(l.geometry))
    .filter((r): r is number[][] => r !== null && r.length > 0);

  if (parentRings.length === 0) return merged; // no parent geometry — skip PIP

  return merged.filter((lot) => {
    const ring = getOuterRing(lot.geometry);
    if (!ring || ring.length === 0) return false;
    const { lng, lat } = ringCentroid(ring);
    return parentRings.some((pr) => pointInRing(lng, lat, pr));
  });
}

/**
 * Detect all subdivision plans within the candidate lots.
 * Returns plans sorted by lot count (descending).
 * Only includes plans with >= MIN_LOTS lots to filter out stray boundary lots.
 *
 * The primary plan (index 0) is used as the estate name.
 * All plans are included so multi-stage or split-lot estates are complete.
 */
// PIP filter (in fetchLotsInParent) already removes neighbouring parcels,
// so the threshold here just drops isolated stray lots (e.g. a single road
// reserve lot from an adjacent plan that shares a corner with the parent).
const MIN_SUBDIVISION_LOTS = 2;

function detectSubdivisionPlans(lots: NswFeature[]): string[] {
  if (lots.length === 0) return [];

  const groups = new Map<string, { count: number; totalArea: number }>();
  for (const lot of lots) {
    const label = lot.properties.planlabel?.toUpperCase();
    if (!label) continue;
    const g = groups.get(label) ?? { count: 0, totalArea: 0 };
    g.count++;
    g.totalArea += lot.properties.planlotarea ?? 0;
    groups.set(label, g);
  }

  return Array.from(groups.entries())
    .filter(([, g]) => g.count >= MIN_SUBDIVISION_LOTS)
    .sort(([, a], [, b]) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.totalArea / a.count - b.totalArea / b.count;
    })
    .map(([label]) => label);
}

/**
 * Convert NSW lot features to a BlocPlus EstateJsonData object.
 */
function buildNswEstateJson(
  center: { lat: number; lng: number },
  subdivisionLots: NswFeature[],
  subdivisionPlanLabel: string,
  address?: string
): EstateJsonData {

  const lots = subdivisionLots
    .filter((f) => f.geometry !== null)
    .map((feature) => {
      const props = feature.properties;
      const ring = getOuterRing(feature.geometry);
      const points = ring ? ringToPoints(ring) : [];

      const rawArea = props.planlotarea ?? 0;
      const units = (props.planlotareaunits ?? '').toLowerCase();
      // Convert hectares to m² if needed
      const landsize = units === 'ha' ? Math.round(rawArea * 10000) : Math.round(rawArea);

      return {
        code: props.lotnumber ?? '',
        handleid: generateHandleId(),
        landsize,
        frontage: 0,
        price: 0,
        status: 'available',
        points,
      };
    })
    .filter((lot) => lot.points.length > 0); // keep lots even if parcelname is null

  return {
    geoLocation: center,
    name: subdivisionPlanLabel,
    stage: '1',
    address: address ?? '',
    website: '',
    status: 'draft',
    lots,
    references: [],
  };
}

// ─── Main entry point ─────────────────────────────────────────────────────────

/**
 * Full NSW estate processing pipeline:
 *  1. Find parent lot(s) → extract bounding box
 *  2. Fetch all candidate lots in bbox (paginated)
 *  3. Detect subdivision plan (highest lot count)
 *  4. Build and return BlocPlus EstateJsonData
 *
 * @param lotNumber      Lot number of the parent lot (e.g. "1"), or "" to search ALL lots in the plan
 * @param planLabel      Plan label of the parent lot (e.g. "DP831464")
 * @param address        Optional address string for the estate metadata
 * @param onProgress     Optional callback for progress messages
 */
export async function processNswEstate(
  lotNumber: string,
  planLabel: string,
  address?: string,
  onProgress?: (message: string) => void
): Promise<EstateJsonData> {
  const normalized = planLabel.toUpperCase();

  let center: { lat: number; lng: number };
  let parentLots: NswFeature[];

  if (lotNumber) {
    // Single parent lot
    onProgress?.(`Locating parent lot (Lot ${lotNumber} ${normalized})...`);
    const parentLot = await findParentLot(lotNumber, normalized);
    parentLots = [parentLot];
    const ring = getOuterRing(parentLot.geometry);
    center = ring ? ringCentroid(ring) : { lat: 0, lng: 0 };
  } else {
    // All lots of the parent plan
    onProgress?.(`Locating all lots in plan ${normalized}...`);
    parentLots = await findAllParentLots(normalized);
    onProgress?.(
      `Found ${parentLots.length} lot(s) in ${normalized} — combining search area...`
    );
    const unionBbox = computeUnionBbox(parentLots);
    center = unionBbox
      ? { lat: (unionBbox.ymin + unionBbox.ymax) / 2, lng: (unionBbox.xmin + unionBbox.xmax) / 2 }
      : { lat: 0, lng: 0 };
  }

  onProgress?.('Searching for subdivision lots within parent boundary...');
  // fetchLotsInParent queries both Cadastre + Pipeline and applies a PIP filter
  // so only lots whose centroid is physically inside a parent parcel survive.
  const subdivisionLots = await fetchLotsInParent(parentLots, normalized);

  if (subdivisionLots.length === 0) {
    throw new Error(
      'No subdivision lots found within the parent lot boundary. ' +
        'The subdivision may not yet be registered, or the lot boundary is too small.'
    );
  }

  // Pick the estate name from the plan with the most lots.
  // Pipeline lots without a DP number use their caseid, which is fine as a
  // secondary label — the DP plan will win if both are present.
  const planCounts = new Map<string, number>();
  for (const lot of subdivisionLots) {
    const label = lot.properties.planlabel?.toUpperCase();
    if (label) planCounts.set(label, (planCounts.get(label) ?? 0) + 1);
  }
  // Prefer DP plan labels over caseid strings for the estate name.
  const sortedPlans = Array.from(planCounts.entries()).sort(([labelA, countA], [labelB, countB]) => {
    const aIsDP = labelA.startsWith('DP');
    const bIsDP = labelB.startsWith('DP');
    if (aIsDP !== bIsDP) return aIsDP ? -1 : 1; // DP plans first
    return countB - countA;                        // then by lot count
  });
  const primaryPlan = sortedPlans[0]?.[0] ?? normalized;
  const planSummary = sortedPlans.map(([p, n]) => `${p} (${n})`).join(' + ');

  onProgress?.(`${planSummary} — ${subdivisionLots.length} lots total. Building estate data...`);

  return buildNswEstateJson(center, subdivisionLots, primaryPlan, address);
}

// ─── Input parsing ────────────────────────────────────────────────────────────

/**
 * Parse user input into lotNumber + planLabel.
 * Accepts:
 *  - "1/DP831464"   (standard NSW lot/plan notation — single specific lot)
 *  - "1 DP831464"   (same)
 *  - "Lot 1 DP831464" (same)
 *  - "DP831464"     (no lot number → lotNumber="" → search ALL lots of that plan)
 */
export function parseNswLotPlan(
  input: string
): { lotNumber: string; planLabel: string } | null {
  const trimmed = input.trim().toUpperCase().replace(/^LOT\s+/, '');

  // "1/DP831464" — standard NSW lot/plan notation
  const slashForm = trimmed.match(/^(\w+)\/(DP\d+)$/);
  if (slashForm) return { lotNumber: slashForm[1], planLabel: slashForm[2] };

  // "1 DP831464" or "Lot 1 DP831464" (LOT prefix stripped above)
  const withLot = trimmed.match(/^(\w+)\s+(DP\d+)$/);
  if (withLot) return { lotNumber: withLot[1], planLabel: withLot[2] };

  // "DP831464" alone — no lot specified, search ALL lots of this plan
  const dpOnly = trimmed.match(/^(DP\d+)$/);
  if (dpOnly) return { lotNumber: '', planLabel: dpOnly[1] };

  return null;
}

/**
 * Returns true if the plan number is a NSW Deposited Plan (DP prefix).
 */
export function isNswPlan(planNumber: string): boolean {
  return planNumber.toUpperCase().startsWith('DP');
}
